/**
 * A monitor's SNMP performance card: the live graphs, the stored 24-hour window, and the toggle.
 *
 * **One implementation for two pages**, which is the rule the OpenSilver `MonitorPerformanceViewModel`
 * already stated: the device page's SNMP tab and the monitor's own page both show this, and
 * duplicating the region is how the two would come to disagree about what a chart means.
 *
 * Watching is the load-bearing part. `WatchMonitor` does two things at once: it puts the monitor's
 * polling agent on the watched cadence, and it makes that agent answer with its whole rolling
 * history - which is why **the graphs open full rather than drawing themselves one point at a
 * time.** Releasing the watch matters just as much: a page that closed without unwatching would
 * leave a customer's agent polling a printer every five seconds for the rest of the day.
 */
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import * as hub from '../../api/hub.js';
import type { MetricsReport, SnmpMonitorDto } from '../../contracts.gen.js';
import { localDateTime, localTime } from '../../format.js';
import { MAINTENANCE_WINDOW_MS, LIVE_WINDOW_MS } from '../../metrics.js';
import { buildMonitorCharts, monitorWaitingText } from '../../monitor-metrics.js';
import { ChartCard } from '../../ui/ChartCard.js';
import { confirm } from '../../ui/dialogs.js';
import type { Disposables } from '../../ui/Disposables.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  hstack,
  muted,
  splitRow,
  stackOf,
  text,
  vstack
} from '../../ui/layout.js';
import { insets, type Desktop, type StackPanel } from 'webwin';

export interface MonitorPerformanceOptions {
  readonly desktop: Desktop;
  readonly dispose: Disposables;
  /** True once the host page has been navigated away from. Checked after every await. */
  cancelled(): boolean;
  /** Nudges the host's layout chain, since a chart appearing changes the page's height. */
  relayout(): void;
}

export interface MonitorPerformanceSection {
  readonly panel: StackPanel;
  /**
   * Points the card at a monitor, subscribing and watching.
   *
   * Idempotent for the same monitor: a `MonitorChanged` re-read must refresh the DTO without
   * stacking subscriptions or re-watching, or every poll report for any of the customer's monitors
   * would add another handler.
   */
  attach(monitor: SnmpMonitorDto): Promise<void>;
  /** Stops watching, so the agent drops back to the monitor's configured interval. */
  detach(): Promise<void>;
}

export function monitorPerformanceSection(
  options: MonitorPerformanceOptions
): MonitorPerformanceSection {
  let monitor: SnmpMonitorDto | null = null;
  let monitorId = 0;
  let live: MetricsReport[] = [];
  let maintenance: MetricsReport[] = [];
  let maintenanceView = false;
  let busy = false;
  /** The hub unsubscribes, dropped and re-made when the card is pointed at a different monitor. */
  let subscriptions: (() => void)[] = [];

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  const liveButton = button('Live · 30 min', () => showLive());
  const maintenanceButton = button('Maintenance · 24 h', () => void showMaintenance());
  const rangeCaption = muted('');
  const maintenanceToggle = button('', () => void toggleMaintenance());

  const controls = splitRow({ align: 'center' });
  fillWith(controls, stackOf(buttonRow(), liveButton, maintenanceButton, rangeCaption));
  controls.controls.add(maintenanceToggle);

  const banner = card({ tint: 'info', bordered: false, padding: insets(GAP.md, GAP.sm) });
  const bannerText = text('', { role: 'info', font: FONT.small, wrap: true });
  banner.controls.add(bannerText);
  banner.visible = false;

  const waiting = muted('');
  const chartHost = vstack({ spacing: GAP.lg, hAlign: 'stretch', autoWidth: false });
  const lastSample = muted('');

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      controls,
      banner,
      status.label,
      waiting,
      chartHost,
      lastSample
    )
  );
  panel.controls.add(body);

  const cards: ChartCard[] = [];

  function source(): MetricsReport[] {
    return maintenanceView ? maintenance : live;
  }

  function render(): void {
    const specs = buildMonitorCharts(
      source(),
      maintenanceView ? MAINTENANCE_WINDOW_MS : LIVE_WINDOW_MS
    );

    if (specs.length !== cards.length) {
      cards.length = 0;
      chartHost.controls.clear();
      for (const spec of specs) {
        const chart = new ChartCard(spec);
        cards.push(chart);
        chartHost.controls.add(chart.panel);
      }
    } else {
      specs.forEach((spec, i) => cards[i]!.apply(spec));
    }

    waiting.text = specs.length === 0 ? monitorWaitingText(source(), maintenanceView) : '';
    waiting.visible = specs.length === 0;

    const newest = source()[source().length - 1];
    lastSample.text = !newest
      ? ''
      : newest.reachable
        ? `Last polled ${localTime(newest.polledUtc)}`
        : `Unreachable at the last poll (${localTime(newest.polledUtc)})`;
    lastSample.visible = lastSample.text.length > 0;

    rangeCaption.text = maintenanceView
      ? `Maintenance history — past 24 hours (${maintenance.length} samples stored)`
      : 'Past 30 minutes';
    liveButton.enabled = maintenanceView;

    options.relayout();
  }

  function applyMonitor(): void {
    const active = monitor?.maintenanceActive ?? false;
    maintenanceToggle.text = active ? 'Stop maintenance mode' : 'Maintenance mode (24 h)';
    maintenanceToggle.enabled = monitor !== null && !busy;
    banner.visible = active;
    bannerText.text = active
      ? `Collecting a stored sample every minute until ${localDateTime(monitor?.maintenanceUntilUtc)}.`
      : '';
  }

  function showLive(): void {
    maintenanceView = false;
    render();
  }

  /** Re-fetched on every switch, so a technician clicking back sees what landed meanwhile. */
  async function showMaintenance(): Promise<void> {
    if (monitorId === 0) return;
    status.busy('Loading the stored 24-hour history…');
    try {
      maintenance = await crm.getMonitorMaintenanceHistory(monitorId, options.dispose.signal());
      if (options.cancelled()) return;
      status.clear();
      maintenanceView = true;
      render();
    } catch (error) {
      if (!options.cancelled()) {
        status.error(describeError(error));
        options.relayout();
      }
    }
  }

  async function toggleMaintenance(): Promise<void> {
    if (!monitor || busy) return;
    const enabling = !monitor.maintenanceActive;
    const ok = await confirm(options.desktop, {
      title: enabling ? 'Maintenance mode' : 'Stop maintenance mode',
      message: enabling
        ? `Store a sample of every reading from ${monitor.displayName} once a minute for the next 24 ` +
          'hours?\n\nThe polling agent keeps collecting with this page closed.'
        : `Stop maintenance mode on ${monitor.displayName}?\n\nWhat it has already stored stays ` +
          'readable for the rest of its 24-hour window.'
    });
    if (!ok || options.cancelled()) return;

    busy = true;
    applyMonitor();
    status.busy(enabling ? 'Turning maintenance mode on…' : 'Turning maintenance mode off…');
    try {
      const updated = await crm.setMonitorMaintenance(monitor.id, enabling);
      if (options.cancelled()) return;
      monitor = updated;
      status.clear();
    } catch (error) {
      if (!options.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      applyMonitor();
      options.relayout();
    }
  }

  function onSample(sample: MetricsReport): void {
    if (sample.monitorId !== monitorId) return;
    live.push(sample);
    // Evicted against the newest sample's OWN timestamp (the polling agent's clock), never the
    // browser's - clock skew between the two would otherwise slide the window.
    const cutoff = Date.parse(sample.polledUtc) - LIVE_WINDOW_MS;
    live = live.filter(s => Date.parse(s.polledUtc) >= cutoff);
    // Rendered in both views, not only the live one: the last-sample line describes what the last
    // POLL did rather than what is on screen, and in the maintenance view no chart rebuild follows
    // to update it on its own.
    render();
  }

  return {
    panel,

    async attach(next: SnmpMonitorDto) {
      const changed = monitorId !== next.id;
      if (changed) await detach();

      monitor = next;
      monitorId = next.id;
      applyMonitor();
      if (!changed) {
        render();
        return;
      }

      // Failure is not fatal: without the hub the card shows the monitor's stored latest readings
      // and its empty state, which is honest, rather than nothing at all.
      if (await hub.ensureConnected()) {
        if (options.cancelled()) return;
        subscriptions = [
          hub.on('MonitorMetricsChanged', sample => onSample(sample)),
          hub.on('MonitorMetricsHistoryChanged', history => {
            if (history.monitorId !== monitorId) return;
            // Replaces rather than merges: the agent's answer IS its buffer.
            live = [...history.samples].sort(
              (a, b) => Date.parse(a.polledUtc) - Date.parse(b.polledUtc)
            );
            render();
          })
        ];
        await hub.watchMonitor(monitorId);
      }
      if (options.cancelled()) return;
      render();
    },

    detach
  };

  async function detach(): Promise<void> {
    if (monitorId === 0) return;
    for (const off of subscriptions) off();
    subscriptions = [];
    const id = monitorId;
    monitorId = 0;
    monitor = null;
    live = [];
    maintenance = [];
    await hub.unwatchMonitor(id);
  }
}
